(() => {
  'use strict';

  // Pixel methods are copied verbatim from media-gallery.js. Keep them in parity
  // with the configurator; scripts/brand-background-removal.browser.test.mjs
  // checks both source parity and the exported pixels. Only this small adapter
  // owns catalogue input validation, conservative no-change results and cleanup.
  class ArtworkPixels {
    clampNumber(value, min, max) {
      return Math.min(Math.max(Number(value) || 0, min), max);
    }

    getArtworkAlphaProfile(data, width, height) {
      const pixelCount = width * height;
      if (!pixelCount) return null;

      let transparent = 0;
      let semiTransparent = 0;
      let edgeTransparent = 0;
      let edgePixels = 0;

      for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex += 1) {
        const x = pixelIndex % width;
        const y = Math.floor(pixelIndex / width);
        const alpha = data[pixelIndex * 4 + 3];
        const isEdge = x === 0 || y === 0 || x === width - 1 || y === height - 1;

        if (alpha <= 2) {
          transparent += 1;
          if (isEdge) edgeTransparent += 1;
        } else if (alpha < 244) {
          semiTransparent += 1;
        }

        if (isEdge) edgePixels += 1;
      }

      return {
        transparentRatio: transparent / pixelCount,
        semiTransparentRatio: semiTransparent / pixelCount,
        edgeTransparentRatio: edgePixels ? edgeTransparent / edgePixels : 0,
      };
    }

    shouldPreserveExistingTransparentArtwork(profile) {
      if (!profile) return false;

      return (
        profile.transparentRatio > 0.012 ||
        profile.edgeTransparentRatio > 0.08 ||
        profile.semiTransparentRatio > 0.035
      );
    }

    estimateArtworkBackgroundColor(data, width, height) {
      const minSide = Math.min(width, height);
      const band = this.clampNumber(Math.round(minSide * 0.055), 2, 28);
      const step = Math.max(1, Math.floor(minSide / 520));
      const cornerLimitX = Math.min(width, band * 3);
      const cornerLimitY = Math.min(height, band * 3);
      const cornerClusters = [];
      const edgeClusters = [];

      const addPixel = (clusters, x, y) => {
        const index = (y * width + x) * 4;
        const alpha = data[index + 3];
        if (alpha < 24) return;

        this.addArtworkBackgroundCluster(clusters, data[index], data[index + 1], data[index + 2], alpha);
      };

      for (let y = 0; y < height; y += step) {
        for (let x = 0; x < width; x += step) {
          const isEdge = x < band || x >= width - band || y < band || y >= height - band;
          if (!isEdge) continue;

          addPixel(edgeClusters, x, y);

          const isCorner =
            (x < cornerLimitX || x >= width - cornerLimitX) &&
            (y < cornerLimitY || y >= height - cornerLimitY);
          if (isCorner) addPixel(cornerClusters, x, y);
        }
      }

      const clusters = cornerClusters.length ? cornerClusters : edgeClusters;
      if (!clusters.length) return null;

      const sortedClusters = clusters
        .filter((cluster) => cluster.weight > 0)
        .sort((a, b) => b.count - a.count);
      const dominantCluster = sortedClusters[0];
      if (!dominantCluster) return null;

      const background = {
        r: dominantCluster.r,
        g: dominantCluster.g,
        b: dominantCluster.b,
        hardTolerance: 24,
        softTolerance: 58,
      };

      const distances = [];
      for (let y = 0; y < height; y += step) {
        for (let x = 0; x < width; x += step) {
          const isEdge = x < band || x >= width - band || y < band || y >= height - band;
          if (!isEdge) continue;

          const index = (y * width + x) * 4;
          if (data[index + 3] < 24) continue;
          distances.push(this.getArtworkColorDistance(data[index], data[index + 1], data[index + 2], background));
        }
      }

      distances.sort((a, b) => a - b);
      const totalClusterCount = sortedClusters.reduce((sum, cluster) => sum + cluster.count, 0) || 1;
      const p50 = distances[Math.floor(distances.length * 0.5)] || 12;
      const p70 = distances[Math.floor(distances.length * 0.7)] || 18;
      const p92 = distances[Math.floor(distances.length * 0.92)] || 42;
      const dominantRatio = this.clampNumber(dominantCluster.count / totalClusterCount, 0, 1);
      const closeEdgeLimit = Math.max(18, p50 * 1.75);
      const closeEdgeRatio = distances.length
        ? distances.filter((distance) => distance <= closeEdgeLimit).length / distances.length
        : 0;
      const edgeUniformity = 1 - this.clampNumber((p92 - 24) / 88, 0, 1);
      const confidence = this.clampNumber(
        dominantRatio * 0.42 + closeEdgeRatio * 0.34 + edgeUniformity * 0.24,
        0,
        1,
      );
      const neutralLight = this.isNeutralLightArtworkBackground(background);
      const isSimple = confidence >= 0.62 || (neutralLight && confidence >= 0.52);
      const conservative = confidence < 0.52;
      const toleranceScale = conservative ? 0.72 : isSimple ? 1 : 0.88;

      background.confidence = confidence;
      background.edgeUniformity = edgeUniformity;
      background.isSimple = isSimple;
      background.conservative = conservative;
      background.isNeutralLight = neutralLight;
      background.hardTolerance = this.clampNumber(Math.max(16, p70 * 0.88) * toleranceScale, 14, conservative ? 34 : 46);
      background.softTolerance = this.clampNumber(
        Math.max(34, p92 * (isSimple ? 1.2 : 0.98), background.hardTolerance + 14),
        background.hardTolerance + 14,
        conservative ? 72 : 96,
      );
      background.residueTolerance = this.clampNumber(
        background.softTolerance * (isSimple ? 1.2 : 1.08),
        background.softTolerance + 8,
        conservative ? 88 : 124,
      );
      background.palette = sortedClusters
        .filter((cluster, index) => {
          if (index === 0) return true;
          if (index > (isSimple ? 5 : 2)) return false;
          if (cluster.count < dominantCluster.count * (isSimple ? 0.065 : 0.14)) return false;
          return this.getArtworkColorDistance(cluster.r, cluster.g, cluster.b, background) <= background.softTolerance * (isSimple ? 0.95 : 0.78);
        })
        .slice(0, isSimple ? 4 : 2)
        .map((cluster) => ({
          r: cluster.r,
          g: cluster.g,
          b: cluster.b,
        }));

      return background;
    }

    addArtworkBackgroundCluster(clusters, r, g, b, alpha = 255) {
      let bestCluster = null;
      let bestDistance = Infinity;

      clusters.forEach((cluster) => {
        const distance = this.getArtworkColorDistance(r, g, b, cluster);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestCluster = cluster;
        }
      });

      if (!bestCluster || bestDistance > 38) {
        clusters.push({
          r,
          g,
          b,
          count: 1,
          weight: alpha,
        });
        return;
      }

      const nextWeight = bestCluster.weight + alpha;
      bestCluster.r = (bestCluster.r * bestCluster.weight + r * alpha) / nextWeight;
      bestCluster.g = (bestCluster.g * bestCluster.weight + g * alpha) / nextWeight;
      bestCluster.b = (bestCluster.b * bestCluster.weight + b * alpha) / nextWeight;
      bestCluster.weight = nextWeight;
      bestCluster.count += 1;
    }

    getConnectedArtworkBackgroundMask(data, width, height, background) {
      const pixelCount = width * height;
      const visited = new Uint8Array(pixelCount);
      const strength = new Uint8Array(pixelCount);
      const queue = new Uint32Array(pixelCount);
      let queueLength = 0;
      const enqueue = (x, y) => {
        if (x < 0 || y < 0 || x >= width || y >= height) return;
        const pixelIndex = y * width + x;
        if (visited[pixelIndex]) return;

        const dataIndex = pixelIndex * 4;
        const alpha = data[dataIndex + 3];
        if (alpha <= 8) {
          visited[pixelIndex] = 1;
          strength[pixelIndex] = 255;
          queue[queueLength++] = pixelIndex;
          return;
        }

        const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
        if (distance > background.softTolerance) return;

        visited[pixelIndex] = 1;
        strength[pixelIndex] = this.getArtworkRemovalStrength(distance, background.hardTolerance, background.softTolerance);
        queue[queueLength++] = pixelIndex;
      };

      for (let x = 0; x < width; x += 1) {
        enqueue(x, 0);
        enqueue(x, height - 1);
      }

      for (let y = 1; y < height - 1; y += 1) {
        enqueue(0, y);
        enqueue(width - 1, y);
      }

      for (let cursor = 0; cursor < queueLength; cursor += 1) {
        const pixelIndex = queue[cursor];
        const x = pixelIndex % width;
        const y = Math.floor(pixelIndex / width);
        enqueue(x + 1, y);
        enqueue(x - 1, y);
        enqueue(x, y + 1);
        enqueue(x, y - 1);
      }

      this.cleanArtworkResidualMask(data, width, height, background, visited, strength);
      this.cleanInteriorArtworkBackgroundPockets(data, width, height, background, visited, strength);
      this.featherArtworkBackgroundMask(data, width, height, background, visited, strength);
      this.stabilizeArtworkRemovalMask(width, height, background, strength);

      return strength;
    }

    cleanArtworkResidualMask(data, width, height, background, visited, strength) {
      const residueTolerance = background.residueTolerance || background.softTolerance * 1.2;
      const conservative = Boolean(background.conservative);
      const confidence = background.confidence ?? 1;
      const neighborThreshold = conservative ? 148 : 112;
      const maxPasses = background.isSimple ? 3 : 2;

      for (let pass = 0; pass < maxPasses; pass += 1) {
        const updates = [];

        for (let y = 1; y < height - 1; y += 1) {
          for (let x = 1; x < width - 1; x += 1) {
            const pixelIndex = y * width + x;
            if (visited[pixelIndex]) continue;

            const neighborStrength = Math.max(
              strength[pixelIndex - 1],
              strength[pixelIndex + 1],
              strength[pixelIndex - width],
              strength[pixelIndex + width],
              strength[pixelIndex - width - 1],
              strength[pixelIndex - width + 1],
              strength[pixelIndex + width - 1],
              strength[pixelIndex + width + 1],
            );
            if (neighborStrength < neighborThreshold) continue;

            const dataIndex = pixelIndex * 4;
            const alpha = data[dataIndex + 3];
            if (alpha <= 10) {
              updates.push({ pixelIndex, strength: 255, visited: true });
              continue;
            }

            const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
            if (distance > residueTolerance) continue;

            const edgeConfidence = this.clampNumber(neighborStrength / 255, 0.42, 1);
            const removalStrength = this.getArtworkRemovalStrength(
              distance,
              Math.max(12, background.hardTolerance * 0.82),
              residueTolerance,
            );
            const residueWeight = background.isSimple ? 0.55 + edgeConfidence * 0.38 : 0.32 + edgeConfidence * 0.34 * confidence;
            const nextStrength = Math.round(removalStrength * residueWeight);

            if (nextStrength > strength[pixelIndex]) {
              updates.push({
                pixelIndex,
                strength: nextStrength,
                visited: nextStrength > (conservative ? 206 : 172),
              });
            }
          }
        }

        if (!updates.length) return;

        updates.forEach((update) => {
          strength[update.pixelIndex] = Math.max(strength[update.pixelIndex], update.strength);
          if (update.visited) visited[update.pixelIndex] = 1;
        });
      }
    }

    cleanInteriorArtworkBackgroundPockets(data, width, height, background, visited, strength) {
      if ((background.confidence ?? 1) < 0.42) return;

      const pixelCount = width * height;
      const checked = new Uint8Array(pixelCount);
      const tolerance = background.isSimple
        ? background.residueTolerance || background.softTolerance * 1.18
        : Math.min(background.softTolerance, background.hardTolerance + 22);
      const maxArea = Math.max(48, Math.round(pixelCount * (background.isSimple ? 0.28 : 0.1)));
      const minArea = 2;
      const queue = [];
      const component = [];

      for (let startIndex = 0; startIndex < pixelCount; startIndex += 1) {
        if (checked[startIndex] || visited[startIndex]) continue;

        const startDataIndex = startIndex * 4;
        const startAlpha = data[startDataIndex + 3];
        if (startAlpha <= 8) {
          checked[startIndex] = 1;
          strength[startIndex] = 255;
          continue;
        }

        const startDistance = this.getArtworkColorDistance(
          data[startDataIndex],
          data[startDataIndex + 1],
          data[startDataIndex + 2],
          background,
        );
        if (startDistance > tolerance) {
          checked[startIndex] = 1;
          continue;
        }

        queue.length = 0;
        component.length = 0;
        queue.push(startIndex);
        checked[startIndex] = 1;
        let touchesEdge = false;
        let distanceTotal = 0;

        for (let cursor = 0; cursor < queue.length; cursor += 1) {
          const pixelIndex = queue[cursor];
          const x = pixelIndex % width;
          const y = Math.floor(pixelIndex / width);
          const dataIndex = pixelIndex * 4;
          const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);

          component.push(pixelIndex);
          distanceTotal += distance;
          if (x <= 1 || y <= 1 || x >= width - 2 || y >= height - 2) touchesEdge = true;

          const enqueuePocketNeighbor = (nextIndex) => {
            if (nextIndex < 0 || nextIndex >= pixelCount || checked[nextIndex] || visited[nextIndex]) return;

            const nextX = nextIndex % width;
            if (Math.abs(nextX - x) > 1) return;

            const nextDataIndex = nextIndex * 4;
            const nextAlpha = data[nextDataIndex + 3];
            if (nextAlpha <= 8) {
              checked[nextIndex] = 1;
              strength[nextIndex] = 255;
              return;
            }

            const nextDistance = this.getArtworkColorDistance(
              data[nextDataIndex],
              data[nextDataIndex + 1],
              data[nextDataIndex + 2],
              background,
            );
            if (nextDistance > tolerance) {
              checked[nextIndex] = 1;
              return;
            }

            checked[nextIndex] = 1;
            queue.push(nextIndex);
          };

          enqueuePocketNeighbor(pixelIndex - 1);
          enqueuePocketNeighbor(pixelIndex + 1);
          enqueuePocketNeighbor(pixelIndex - width);
          enqueuePocketNeighbor(pixelIndex + width);

          if (component.length > maxArea * 1.45 && !background.isSimple) break;
        }

        if (touchesEdge || component.length < minArea || component.length > maxArea) continue;

        const averageDistance = distanceTotal / Math.max(component.length, 1);
        const componentPressure = component.reduce(
          (pressure, pixelIndex) => Math.max(pressure, this.getNearbyArtworkRemovalPressure(strength, width, height, pixelIndex, 2)),
          0,
        );
        const shouldRemove =
          averageDistance <= background.hardTolerance * (background.isSimple ? 1.65 : 1.25) ||
          componentPressure >= (background.isSimple ? 72 : 128);

        if (!shouldRemove) continue;

        component.forEach((pixelIndex) => {
          const dataIndex = pixelIndex * 4;
          const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
          const removalStrength = this.getArtworkRemovalStrength(
            distance,
            Math.max(10, background.hardTolerance * 0.72),
            tolerance,
          );
          const nextStrength = Math.round(removalStrength * (background.isSimple ? 0.94 : 0.68));
          strength[pixelIndex] = Math.max(strength[pixelIndex], nextStrength);
          if (nextStrength > 180) visited[pixelIndex] = 1;
        });
      }
    }

    featherArtworkBackgroundMask(data, width, height, background, visited, strength) {
      const updates = [];
      const softTolerance = background.residueTolerance || background.softTolerance * 1.18;
      const conservative = Boolean(background.conservative);
      const neighborThreshold = conservative ? 176 : 150;

      for (let y = 1; y < height - 1; y += 1) {
        for (let x = 1; x < width - 1; x += 1) {
          const pixelIndex = y * width + x;
          if (visited[pixelIndex]) continue;

          const neighborStrength = Math.max(
            strength[pixelIndex - 1],
            strength[pixelIndex + 1],
            strength[pixelIndex - width],
            strength[pixelIndex + width],
          );
          if (neighborStrength < neighborThreshold) continue;

          const dataIndex = pixelIndex * 4;
          const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
          if (distance > softTolerance) continue;

          updates.push({
            pixelIndex,
            strength: Math.round(
              this.getArtworkRemovalStrength(distance, background.hardTolerance * 0.9, softTolerance) *
                this.clampNumber(neighborStrength / 255, conservative ? 0.3 : 0.42, conservative ? 0.58 : 0.8),
            ),
          });
        }
      }

      updates.forEach((update) => {
        strength[update.pixelIndex] = Math.max(strength[update.pixelIndex], update.strength);
      });
    }

    stabilizeArtworkRemovalMask(width, height, background, strength) {
      if (background.isSimple) return;

      let strongPixels = 0;
      for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
        if (strength[pixelIndex] > 235) strongPixels += 1;
      }

      const strongRatio = strongPixels / Math.max(width * height, 1);
      if (strongRatio < 0.92) return;

      for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
        strength[pixelIndex] = Math.round(strength[pixelIndex] * 0.58);
      }
    }

    cleanGlobalArtworkBackgroundResidues(data, width, height, background, strength) {
      const confidence = background.confidence ?? 1;
      if (confidence < 0.38) return;

      const isNeutralLightBackground = background.isNeutralLight ?? this.isNeutralLightArtworkBackground(background);
      const globalWeight = background.isSimple ? 1 : this.clampNumber((confidence - 0.34) / 0.34, 0.22, 0.72);
      const hardResidueTolerance = isNeutralLightBackground
        ? Math.min(background.hardTolerance * (background.isSimple ? 1.05 : 0.82), background.residueTolerance || background.softTolerance)
        : background.hardTolerance * 0.72;
      const softResidueTolerance = isNeutralLightBackground
        ? Math.min(background.softTolerance * (background.isSimple ? 1.02 : 0.84), background.residueTolerance || background.softTolerance)
        : background.hardTolerance * 0.88;

      for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
        if (strength[pixelIndex] > 235) continue;

        const dataIndex = pixelIndex * 4;
        const alpha = data[dataIndex + 3];
        if (alpha <= 8) {
          strength[pixelIndex] = 255;
          continue;
        }

        const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
        if (distance <= hardResidueTolerance) {
          const targetStrength = isNeutralLightBackground ? Math.round(255 * globalWeight) : Math.round(218 * globalWeight);
          strength[pixelIndex] = Math.max(strength[pixelIndex], targetStrength);
          continue;
        }

        if (!isNeutralLightBackground || distance > softResidueTolerance) continue;

        const edgePressure = this.getNearbyArtworkRemovalPressure(strength, width, height, pixelIndex, 2);
        if (edgePressure < (background.isSimple ? 118 : 164)) continue;

        const removalStrength = this.getArtworkRemovalStrength(distance, hardResidueTolerance, softResidueTolerance);
        strength[pixelIndex] = Math.max(
          strength[pixelIndex],
          Math.round(removalStrength * this.clampNumber(edgePressure / 255, 0.36, background.isSimple ? 0.86 : 0.56) * globalWeight),
        );
      }
    }

    getNearbyArtworkRemovalPressure(strength, width, height, pixelIndex, radius = 1) {
      const x = pixelIndex % width;
      const y = Math.floor(pixelIndex / width);
      let pressure = 0;

      for (let offsetY = -radius; offsetY <= radius; offsetY += 1) {
        const nextY = y + offsetY;
        if (nextY < 0 || nextY >= height) continue;

        for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
          if (!offsetX && !offsetY) continue;
          const nextX = x + offsetX;
          if (nextX < 0 || nextX >= width) continue;
          pressure = Math.max(pressure, strength[nextY * width + nextX]);
        }
      }

      return pressure;
    }

    isNeutralLightArtworkBackground(background) {
      const luma = background.r * 0.299 + background.g * 0.587 + background.b * 0.114;
      const maxChannel = Math.max(background.r, background.g, background.b);
      const minChannel = Math.min(background.r, background.g, background.b);
      return luma >= 188 && maxChannel - minChannel <= 42;
    }

    refineArtworkEdgeMatte(data, width, height, background, mask) {
      // Recover coverage from C = alpha * F + (1 - alpha) * B. Only accept
      // a nearby ink sample when all RGB channels fit the same matte. This
      // removes the baked-in background fringe without blurring solid ink.
      if (background.skipEdgeMatte || !background.isSimple || background.edgeUniformity < 0.75) return;
      const refined = new Uint8Array(width * height);
      for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
        const index = pixelIndex * 4;
        if (data[index + 3] < 244) continue;
        const r = data[index], g = data[index + 1], b = data[index + 2];
        const sample = this.getNearestArtworkBackgroundColor(r, g, b, background);
        const cr = r - sample.r, cg = g - sample.g, cb = b - sample.b;
        const magnitude = cr * cr + cg * cg + cb * cb;
        if (magnitude < 12 || this.getNearbyArtworkRemovalPressure(mask, width, height, pixelIndex, 1) < 200) continue;
        const x = pixelIndex % width, y = Math.floor(pixelIndex / width);
        let bestMagnitude = magnitude, coverage = 1;
        for (let dy = -3; dy <= 3; dy += 1) {
          const sy = y + dy;
          if (sy < 0 || sy >= height) continue;
          for (let dx = -3; dx <= 3; dx += 1) {
            const sx = x + dx;
            if (sx < 0 || sx >= width || (!dx && !dy)) continue;
            const neighbor = sy * width + sx, ni = neighbor * 4;
            if (mask[neighbor] > 8 || data[ni + 3] < 250) continue;
            const fr = data[ni] - sample.r, fg = data[ni + 1] - sample.g, fb = data[ni + 2] - sample.b;
            const fm = fr * fr + fg * fg + fb * fb;
            if (fm <= bestMagnitude + 16) continue;
            const alpha = (cr * fr + cg * fg + cb * fb) / fm;
            if (alpha <= 0 || alpha >= 0.995) continue;
            const er = cr - alpha * fr, eg = cg - alpha * fg, eb = cb - alpha * fb;
            if (er * er + eg * eg + eb * eb > 27) continue;
            bestMagnitude = fm;
            coverage = alpha;
          }
        }
        if (coverage < 1) {
          mask[pixelIndex] = Math.round((1 - coverage) * 255);
          refined[pixelIndex] = 1;
        }
      }
      mask.refinedEdges = refined;
    }

    applyArtworkBackgroundRemoval(data, width, height, background, mask) {
      this.refineArtworkEdgeMatte(data, width, height, background, mask);
      for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
        const removalStrength = mask[pixelIndex];
        if (!removalStrength) continue;

        const dataIndex = pixelIndex * 4;
        const alpha = data[dataIndex + 3];
        if (!alpha) continue;

        const originalRed = data[dataIndex];
        const originalGreen = data[dataIndex + 1];
        const originalBlue = data[dataIndex + 2];
        const originalDistance = this.getArtworkColorDistance(originalRed, originalGreen, originalBlue, background);
        const backgroundSample = this.getNearestArtworkBackgroundColor(originalRed, originalGreen, originalBlue, background);
        const removalRatio = removalStrength / 255;
        let nextAlpha = Math.round(alpha * (1 - removalRatio));

        if (nextAlpha > 2 && (removalRatio > 0.08 || mask.refinedEdges?.[pixelIndex])) {
          const retainedRatio = this.clampNumber(nextAlpha / Math.max(alpha, 1), mask.refinedEdges?.[pixelIndex] ? 1 / 255 : background.conservative ? 0.12 : 0.06, 1);
          data[dataIndex] = this.clampColorChannel((originalRed - backgroundSample.r * (1 - retainedRatio)) / retainedRatio);
          data[dataIndex + 1] = this.clampColorChannel((originalGreen - backgroundSample.g * (1 - retainedRatio)) / retainedRatio);
          data[dataIndex + 2] = this.clampColorChannel((originalBlue - backgroundSample.b * (1 - retainedRatio)) / retainedRatio);
        }

        const snapAlpha = background.isSimple ? 16 : 10;
        if (!mask.refinedEdges?.[pixelIndex] && nextAlpha <= snapAlpha && originalDistance <= (background.residueTolerance || background.softTolerance) * (background.isSimple ? 1.05 : 0.92)) {
          nextAlpha = 0;
        }

        data[dataIndex + 3] = nextAlpha;

        if (nextAlpha <= 2) {
          data[dataIndex] = Math.round(background.r);
          data[dataIndex + 1] = Math.round(background.g);
          data[dataIndex + 2] = Math.round(background.b);
          data[dataIndex + 3] = 0;
        }
      }
    }

    polishArtworkForegroundEdges(data, width, height, background, mask) {
      if ((background.confidence ?? 1) < 0.48) return;

      const isNeutralLightBackground = background.isNeutralLight ?? this.isNeutralLightArtworkBackground(background);
      const tolerance = isNeutralLightBackground
        ? Math.min((background.residueTolerance || background.softTolerance) * 1.08, background.softTolerance + 26)
        : background.softTolerance * 0.82;
      const updates = [];

      for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
        const dataIndex = pixelIndex * 4;
        const alpha = data[dataIndex + 3];
        if (alpha <= 8 || mask[pixelIndex] > 128 || mask.refinedEdges?.[pixelIndex]) continue;

        const edgePressure = this.getNearbyArtworkRemovalPressure(mask, width, height, pixelIndex, 1);
        if (edgePressure < (background.isSimple ? 138 : 184)) continue;

        const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
        if (distance > tolerance) continue;

        const removalRatio =
          (this.getArtworkRemovalStrength(distance, Math.max(8, background.hardTolerance * 0.48), tolerance) / 255) *
          this.clampNumber(edgePressure / 255, 0.28, background.isSimple ? 0.58 : 0.34);
        if (removalRatio <= 0.05) continue;

        updates.push({ pixelIndex, removalRatio });
      }

      updates.forEach(({ pixelIndex, removalRatio }) => {
        const dataIndex = pixelIndex * 4;
        const alpha = data[dataIndex + 3];
        const backgroundSample = this.getNearestArtworkBackgroundColor(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
        const nextAlpha = Math.round(alpha * (1 - removalRatio));
        const retainedRatio = this.clampNumber(nextAlpha / Math.max(alpha, 1), 0.08, 1);

        data[dataIndex] = this.clampColorChannel((data[dataIndex] - backgroundSample.r * (1 - retainedRatio)) / retainedRatio);
        data[dataIndex + 1] = this.clampColorChannel((data[dataIndex + 1] - backgroundSample.g * (1 - retainedRatio)) / retainedRatio);
        data[dataIndex + 2] = this.clampColorChannel((data[dataIndex + 2] - backgroundSample.b * (1 - retainedRatio)) / retainedRatio);
        data[dataIndex + 3] = nextAlpha <= 8 ? 0 : nextAlpha;
      });
    }

    getArtworkRemovalStrength(distance, hardTolerance, softTolerance) {
      if (distance <= hardTolerance) return 255;
      if (distance >= softTolerance) return 0;

      const progress = (distance - hardTolerance) / Math.max(softTolerance - hardTolerance, 1);
      const eased = progress * progress * (3 - 2 * progress);
      return Math.round((1 - eased) * 255);
    }

    getArtworkColorDistance(r, g, b, background) {
      const getDistance = (sample) => {
        const dr = r - sample.r;
        const dg = g - sample.g;
        const db = b - sample.b;
        const colorDistance = Math.sqrt(dr * dr * 0.32 + dg * dg * 0.5 + db * db * 0.18);
        const luma = r * 0.299 + g * 0.587 + b * 0.114;
        const backgroundLuma = sample.r * 0.299 + sample.g * 0.587 + sample.b * 0.114;
        return colorDistance + Math.abs(luma - backgroundLuma) * 0.22;
      };

      if (Array.isArray(background.palette) && background.palette.length) {
        return background.palette.reduce((bestDistance, sample) => Math.min(bestDistance, getDistance(sample)), Infinity);
      }

      return getDistance(background);
    }

    getNearestArtworkBackgroundColor(r, g, b, background) {
      const samples = Array.isArray(background.palette) && background.palette.length ? background.palette : [background];

      return samples.reduce(
        (best, sample) => {
          const distance = this.getArtworkColorDistance(r, g, b, sample);
          return distance < best.distance ? { sample, distance } : best;
        },
        { sample: background, distance: Infinity },
      ).sample;
    }

    clampColorChannel(value) {
      return Math.min(Math.max(Math.round(Number(value) || 0), 0), 255);
    }
  }

  const pixels = new ArtworkPixels();
  const MAX_INPUT_BYTES = 5 * 1024 * 1024;
  const MAX_INPUT_PIXELS = 16000000;
  const MAX_INPUT_EDGE = 8192;
  const RASTER_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

  function assertCurrent({ signal, isCurrent } = {}) {
    if (signal?.aborted || (typeof isCurrent === 'function' && !isCurrent())) {
      const error = new Error(signal?.aborted ? 'Artwork preparation cancelled' : 'Artwork preparation superseded');
      error.name = 'AbortError';
      throw error;
    }
  }

  async function checkpoint(options) {
    assertCurrent(options);
    await new Promise((resolve) => window.setTimeout(resolve, 0));
    assertCurrent(options);
  }

  // A proposal keeps the complete canvas. Only pixels connected to an exterior
  // edge are candidates: interior/global cleanup cannot infer artistic intent.
  // Yield the flood in chunks; use the existing colour and matte helpers below.
  async function exteriorMask(data, width, height, background, options) {
    const visited = new Uint8Array(width * height);
    const mask = new Uint8Array(width * height);
    const queue = new Uint32Array(width * height);
    let tail = 0;
    const enqueue = (index) => {
      if (visited[index]) return;
      visited[index] = 1;
      const offset = index * 4;
      const distance = pixels.getArtworkColorDistance(data[offset], data[offset + 1], data[offset + 2], background);
      if (distance > background.softTolerance) return;
      mask[index] = pixels.getArtworkRemovalStrength(distance, background.hardTolerance, background.softTolerance);
      queue[tail++] = index;
    };
    for (let x = 0; x < width; x += 1) { enqueue(x); enqueue((height - 1) * width + x); }
    for (let y = 1; y < height - 1; y += 1) { enqueue(y * width); enqueue(y * width + width - 1); }
    for (let cursor = 0; cursor < tail; cursor += 1) {
      const index = queue[cursor];
      const x = index % width;
      if (x > 0) enqueue(index - 1);
      if (x < width - 1) enqueue(index + 1);
      if (index >= width) enqueue(index - width);
      if (index < width * (height - 1)) enqueue(index + width);
      if ((cursor + 1) % 65536 === 0) await checkpoint(options);
    }
    return mask;
  }

  async function prepareArtworkPixels(input, options = {}) {
    const { background: backgroundAction = 'keep' } = options;
    assertCurrent(options);
    if (backgroundAction !== 'keep' && backgroundAction !== 'remove') throw new Error('Unknown background preparation action');
    const { width, height } = input || {};
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 ||
      width * height > MAX_INPUT_PIXELS || Math.max(width, height) > MAX_INPUT_EDGE ||
      (!Array.isArray(input.pixels) && !ArrayBuffer.isView(input.pixels)) || input.pixels.length !== width * height * 4) {
      throw new Error('Invalid artwork pixels: up to 16 million pixels and an 8192 pixel edge');
    }
    // Snapshot before the first await: an editor can change its input buffer
    // while this proposal runs without changing the exact version processed.
    const byteInput = input.pixels instanceof Uint8Array || input.pixels instanceof Uint8ClampedArray;
    const snapshot = byteInput ? null : input.pixels.slice();
    const data = byteInput ? new Uint8ClampedArray(input.pixels) : new Uint8ClampedArray(snapshot.length);
    if (snapshot) {
      for (let index = 0; index < snapshot.length; index += 1) {
        const channel = snapshot[index];
        if (!Number.isInteger(channel) || channel < 0 || channel > 255) throw new Error('Invalid artwork pixel channel');
        data[index] = channel;
        if ((index + 1) % 262144 === 0) await checkpoint(options);
      }
    }
    const result = (changed, reason, diagnostics = {}, output = data) => ({
      pixels: output, width, height, changed, reason, requiresApproval: changed,
      productionReady: false,
      warnings: backgroundAction === 'remove' ? ['WHITE_BACKGROUND_INTENT_UNKNOWN'] : [],
      diagnostics: { method: 'exterior-connected-v1', backgroundAction, interiorCleanup: false, ...diagnostics },
    });
    if (backgroundAction === 'keep') return result(false, 'background-kept');
    await checkpoint(options);
    // Even a single intentionally translucent pixel is preserved. A flattened
    // matte cannot be inferred reliably from already transparent artwork.
    for (let index = 3; index < data.length; index += 4) {
      if (data[index] < 255) return result(false, 'existing-transparency-preserved', { uncertain: true });
      if ((index + 1) % 262144 === 0) await checkpoint(options);
    }
    const background = pixels.estimateArtworkBackgroundColor(data, width, height);
    if (!background?.isSimple) return result(false, 'background-uncertain', { uncertain: true });
    await checkpoint(options);
    const mask = await exteriorMask(data, width, height, background, options);
    const output = new Uint8ClampedArray(data);
    const rowsPerChunk = Math.max(1, Math.floor(65536 / width));
    // Three source rows of context preserve the native matte neighbourhood at
    // chunk boundaries. Read every tile from the immutable snapshot, never from
    // the previous tile's converted pixels. No crop, scale or interior polish.
    for (let top = 0; top < height; top += rowsPerChunk) {
      const bottom = Math.min(height, top + rowsPerChunk);
      const from = Math.max(0, top - 3), to = Math.min(height, bottom + 3);
      const tile = data.slice(from * width * 4, to * width * 4);
      const tileMask = mask.slice(from * width, to * width);
      pixels.applyArtworkBackgroundRemoval(tile, width, to - from, background, tileMask);
      output.set(tile.subarray((top - from) * width * 4, (bottom - from) * width * 4), top * width * 4);
      await checkpoint(options);
    }
    let removedPixels = 0, retainedOpacity = 0, preservedInteriorPixels = 0;
    for (let index = 0; index < width * height; index += 1) {
      const offset = index * 4;
      // Matte recovery may reach across a very thin dark outline. Restore every
      // enclosed background-like pixel exactly, including a one-pixel reserve.
      if (!mask[index] && pixels.getArtworkColorDistance(data[offset], data[offset + 1], data[offset + 2], background) <= background.softTolerance) {
        output.set(data.subarray(offset, offset + 4), offset);
        preservedInteriorPixels += 1;
      }
      if (output[offset + 3] <= 2) removedPixels += 1;
      retainedOpacity += output[offset + 3] / 255;
      if ((index + 1) % 65536 === 0) await checkpoint(options);
    }
    assertCurrent(options);
    if (!removedPixels || retainedOpacity < Math.max(4, width * height * 0.005)) {
      return result(false, 'insufficient-foreground', { uncertain: true });
    }
    return result(true, 'background-removal-proposed', {
      uncertain: true, removedPixels, preservedInteriorPixels,
      backgroundColor: { r: Math.round(background.r), g: Math.round(background.g), b: Math.round(background.b) },
      colorUniformityConfidence: background.confidence,
    }, output);
  }

  async function validateRasterSignature(file) {
    const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const matches = (signature, offset = 0) => signature.every((value, index) => bytes[index + offset] === value);
    const valid = file.type === 'image/png'
      ? matches([137, 80, 78, 71, 13, 10, 26, 10])
      : file.type === 'image/jpeg'
        ? matches([255, 216, 255])
        : matches([82, 73, 70, 70]) && matches([87, 69, 66, 80], 8);
    if (!valid) throw new Error('Logo content does not match its raster file type');
  }

  function loadImage(image, url, options) {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        window.clearTimeout(timeout);
        options?.signal?.removeEventListener('abort', cancel);
        image.onload = null;
        image.onerror = null;
      };
      const cancel = () => {
        cleanup();
        try { assertCurrent(options); } catch (error) { reject(error); }
      };
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('Logo image loading timed out'));
      }, 8000);
      image.onload = () => {
        cleanup();
        resolve();
      };
      image.onerror = () => {
        cleanup();
        reject(new Error('Logo image loading failed'));
      };
      options?.signal?.addEventListener('abort', cancel, { once: true });
      image.src = url;
    });
  }

  function exportPNG(canvas, options) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error, blob) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        options.signal?.removeEventListener('abort', cancel);
        if (error) reject(error); else resolve(blob);
      };
      const cancel = () => { try { assertCurrent(options); } catch (error) { finish(error); } };
      const timeout = window.setTimeout(() => finish(new Error('Logo background removal export timed out')), 8000);
      options.signal?.addEventListener('abort', cancel, { once: true });
      try {
        assertCurrent(options);
        canvas.toBlob((blob) => finish(null, blob), 'image/png');
      } catch (error) { finish(error); }
    });
  }

  async function remove(file, options = {}) {
    assertCurrent(options);
    if (options.background !== undefined && !['keep', 'remove'].includes(options.background)) throw new Error('Unknown background preparation action');
    if (!(file instanceof File) || !RASTER_TYPES.has(file.type)) {
      throw new Error('Background removal requires a PNG, JPEG or WebP file');
    }
    if (!file.size || file.size > MAX_INPUT_BYTES) {
      throw new Error('Logo file must be non-empty and no larger than 5 MiB');
    }
    await validateRasterSignature(file);
    assertCurrent(options);
    if (options.background === 'keep') return { file, changed: false, reason: 'background-kept', requiresApproval: false, productionReady: false, warnings: [] };

    let url;
    let image;
    let canvas;
    try {
      url = URL.createObjectURL(file);
      image = new Image();
      await loadImage(image, url, options);
      assertCurrent(options);
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      if (!width || !height || width * height > MAX_INPUT_PIXELS || Math.max(width, height) > MAX_INPUT_EDGE) {
        throw new Error('Background removal supports up to 16 million pixels and an 8192 pixel edge');
      }

      const yieldToBrowser = () => checkpoint(options);
      // Match the configurator: preflight a sample, then process original pixels.
      // The sample is never exported and never resized into a production mask.
      canvas = document.createElement('canvas');
      const context = canvas.getContext?.('2d', { willReadFrequently: true });
      if (!context) throw new Error('Canvas unavailable for logo background removal');
      const sampleScale = Math.min(1, 512 / Math.max(width, height));
      canvas.width = Math.max(1, Math.round(width * sampleScale));
      canvas.height = Math.max(1, Math.round(height * sampleScale));
      context.imageSmoothingEnabled = false;
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const sample = context.getImageData(0, 0, canvas.width, canvas.height);
      if (options.background !== 'remove') {
        if (pixels.shouldPreserveExistingTransparentArtwork(pixels.getArtworkAlphaProfile(sample.data, canvas.width, canvas.height))) return { file, changed: false };
        if (!pixels.estimateArtworkBackgroundColor(sample.data, canvas.width, canvas.height)?.isSimple) return { file, changed: false };
      }

      await yieldToBrowser();
      canvas.width = width;
      canvas.height = height;
      context.drawImage(image, 0, 0);
      const imageData = context.getImageData(0, 0, width, height);
      const data = imageData.data;
      let proposal;
      if (options.background === 'remove') {
        proposal = await prepareArtworkPixels({ width, height, pixels: data }, options);
        if (!proposal.changed) { const { pixels: _pixels, ...metadata } = proposal; return { file, ...metadata }; }
        data.set(proposal.pixels);
      } else {
      const profile = pixels.getArtworkAlphaProfile(data, width, height);
      if (pixels.shouldPreserveExistingTransparentArtwork(profile)) return { file, changed: false };
      const background = pixels.estimateArtworkBackgroundColor(data, width, height);
      if (!background?.isSimple) return { file, changed: false };

      const originalAlpha = new Uint8Array(width * height);
      let originalOpaquePixels = 0;
      for (let index = 0; index < originalAlpha.length; index += 1) {
        originalAlpha[index] = data[index * 4 + 3];
        if (originalAlpha[index] >= 244) originalOpaquePixels += 1;
      }
      await yieldToBrowser();
      const mask = pixels.getConnectedArtworkBackgroundMask(data, width, height, background);
      await yieldToBrowser();
      pixels.cleanGlobalArtworkBackgroundResidues(data, width, height, background, mask);
      pixels.applyArtworkBackgroundRemoval(data, width, height, background, mask);
      await yieldToBrowser();
      pixels.polishArtworkForegroundEdges(data, width, height, background, mask);

      let removedOpaquePixels = 0;
      let retainedOpacity = 0;
      for (let index = 0; index < originalAlpha.length; index += 1) {
        const alpha = data[index * 4 + 3];
        if (originalAlpha[index] >= 244 && alpha <= 2) removedOpaquePixels += 1;
        retainedOpacity += alpha / 255;
      }
      // Never replace the logo with a blank image or report a conversion as a
      // successful removal. Very sparse results are left for manual editing.
      if (!removedOpaquePixels || retainedOpacity < Math.max(4, originalOpaquePixels * 0.005)) {
        return { file, changed: false };
      }
      }

      context.putImageData(imageData, 0, 0);
      await yieldToBrowser();
      const blob = await exportPNG(canvas, options);
      assertCurrent(options);
      if (!blob) throw new Error('Logo background removal export failed');
      if (blob.size > MAX_INPUT_BYTES) throw new Error('Transparent logo exceeds the 5 MiB file limit');
      const baseName = String(file.name || 'logo').replace(/\.[a-z0-9]+$/i, '').trim() || 'logo';
      return {
        file: new File([blob], `${baseName}-transparent.png`, { type: 'image/png', lastModified: Date.now() }),
        changed: true,
        ...(proposal ? { reason: proposal.reason, requiresApproval: true, productionReady: false, warnings: proposal.warnings, diagnostics: proposal.diagnostics, width, height } : {}),
      };
    } finally {
      if (image) {
        image.onload = null;
        image.onerror = null;
        image.src = '';
      }
      if (url) URL.revokeObjectURL(url);
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
    }
  }

  // Bounded pure adapter shared with the server preview cache. This uses the
  // exact configurator pixel methods above; no second removal algorithm exists.
  function preparePixels(input, { allowBackgroundRemoval = true } = {}) {
    const { width, height } = input || {};
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 ||
      Math.max(width, height) > 1024 || input.pixels?.length !== width * height * 4) {
      throw new Error('Invalid logo preview pixels');
    }
    for (const value of input.pixels) if (!Number.isInteger(value) || value < 0 || value > 255) throw new Error('Invalid logo preview pixels');
    const data = new Uint8ClampedArray(input.pixels);
    let edge = 0, neutralEdge = 0, neutralCorners = 0, darkestEdge = 255, lightestEdge = 0, transparent = 0;
    for (let index = 0; index < data.length; index += 4) {
      const x = (index / 4) % width, y = Math.floor(index / 4 / width);
      if (data[index + 3] < 250) transparent++;
      if (x !== 0 && y !== 0 && x !== width - 1 && y !== height - 1) continue;
      edge++;
      const low = Math.min(data[index], data[index + 1], data[index + 2]);
      const high = Math.max(data[index], data[index + 1], data[index + 2]);
      if (data[index + 3] === 255 && low >= 235 && high - low <= 8) {
        neutralEdge++;
        if ((x === 0 || x === width - 1) && (y === 0 || y === height - 1)) neutralCorners++;
        darkestEdge = Math.min(darkestEdge, low); lightestEdge = Math.max(lightestEdge, high);
      }
    }
    const lightNeutralMatte = neutralCorners === 4 && neutralEdge / edge >= .97 && lightestEdge - darkestEdge <= 8;
    let backgroundRemoved = false;
    if (allowBackgroundRemoval && transparent === 0 && lightNeutralMatte) {
      const background = pixels.estimateArtworkBackgroundColor(data, width, height);
      if (background?.isSimple) {
        background.skipEdgeMatte = true;
        const cutout = new Uint8ClampedArray(data);
        const mask = pixels.getConnectedArtworkBackgroundMask(cutout, width, height, background);
        pixels.cleanGlobalArtworkBackgroundResidues(cutout, width, height, background, mask);
        pixels.applyArtworkBackgroundRemoval(cutout, width, height, background, mask);
        pixels.polishArtworkForegroundEdges(cutout, width, height, background, mask);
        let removed = 0, retainedOpacity = 0;
        for (let index = 3; index < cutout.length; index += 4) {
          if (cutout[index] <= 2) removed++;
          retainedOpacity += cutout[index] / 255;
        }
        if (removed && retainedOpacity >= Math.max(4, width * height * .005)) {
          data.set(cutout); backgroundRemoved = true;
        }
      }
    }
    let left = width, top = height, right = -1, bottom = -1, brightness = 0, weight = 0, hasTransparency = false;
    for (let index = 0; index < data.length; index += 4) {
      const alpha = data[index + 3];
      if (alpha < 250) hasTransparency = true;
      if (alpha <= 8) continue;
      const x = (index / 4) % width, y = Math.floor(index / 4 / width);
      left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y);
      brightness += (data[index] + data[index + 1] + data[index + 2]) / 3 * alpha; weight += alpha;
    }
    if (right < left || weight < 4 * 255) throw new Error('Empty logo preview');
    const croppedWidth = right - left + 1, croppedHeight = bottom - top + 1;
    const cropped = new Uint8ClampedArray(croppedWidth * croppedHeight * 4);
    for (let y = 0; y < croppedHeight; y++) {
      const start = ((y + top) * width + left) * 4;
      cropped.set(data.subarray(start, start + croppedWidth * 4), y * croppedWidth * 4);
    }
    return { pixels: cropped, width: croppedWidth, height: croppedHeight, hasTransparency,
      previewOnDark: hasTransparency && brightness / weight > 220, backgroundRemoved,
      marginsAdjusted: left > 0 || top > 0 || right < width - 1 || bottom < height - 1 };
  }

  window.TodayMerchLogoBackground = Object.freeze({ remove, preparePixels, prepareArtworkPixels });
})();
